/**
 * Service pour la gestion des commerces/restaurants.
 *
 * Fournit des méthodes pour :
 * - createCommerce : créer un nouveau commerce.
 * - createCommerceContact : ajouter un contact à un commerce existant.
 * - createCommerceLogo : ajouter ou mettre à jour le logo d’un commerce.
 * - DeleteCommerce : supprimer un commerce.
 * - UpdateCommerce : mettre à jour les informations d’un commerce.
 * - getCommerces : récupérer la liste des commerces avec pagination.
 *
 * Utilise axiosInstance pour les requêtes HTTP avec interceptors configurés.
 * Types utilisés :
 * - ICommerceBody : structure des données d’un commerce.
 * - IContact : structure d’un contact de commerce.
 * - ICommerceLogo : structure pour l’upload d’un logo.
 * - IApiResponseRestaurant : structure de la réponse API pour les commerces.
 * - AxiosError : type pour gérer les erreurs des requêtes HTTP.
 *
 * Références externes :
 * - token : contient les tokens d’authentification.
 * - idToAddValue : identifiant du commerce pour certaines opérations.
 * - pageCommerce : numéro de page pour la pagination.
 */
import { toRaw } from 'vue';
import type {
  IApiResponseAllCommerceSlugs,
  IApiResponseCommerceSlugs,
  IApiResponseRestaurant,
  ICommerceBodySlug,
  ICommerceBodyUpdate,
  ICommerceSlug,
} from '@/types/commerce.types';
import type { AxiosError, AxiosResponse } from 'axios';
import axiosInstance from '@/services/interceptor.services';

import type {
  ICommerceBody,
  IContact,
  ICommerceLogo,
  IAddStoreContactPayload,
} from '@/types/commerce.types';
import { token, restaurantId } from '@/refs/common.refs';
import axios from 'axios';
import { VITE_API_URL } from '@/utils/runtimeConfig';
import {
  addAfterSlug,
  addBeforeSlug,
  commerceId,
  contactId,
  count,
  idToAddValue,
  orderSlug,
  pageCommerce,
  pageSlug,
  qSlug,
  slug,
  slugId,
  sortBySlug,
  text,
  paginationCommerce,
  pageSizeCommerce,
  designationCommerce,
  slugCommerce,
  organizationIdCommerce,
  unaffiliatedCommerce,
  sortByCommerce,
  orderCommerce,
  slugStoreId,
  commerceIdHorraire,
} from '@/refs/commerce.refs';

export const CommerceService = {
  /**
   * Crée un nouveau commerce.
   * @param data - Informations du commerce à créer (ICommerceBody)
   * @returns Promise contenant la réponse Axios ou l'erreur
   */
  createCommerce: async (data: ICommerceBody) => {
    try {
      const response = axiosInstance.post(`restaurants/create`, data, {
        headers: { 'Content-Type': 'application/json' },
      });
      return response;
    } catch (error) {
      const err = error as AxiosError;
      return Promise.reject(err);
    }
  },

  /**
   * Ajoute un contact à un commerce existant.
   * @param data - Données du contact (IContact)
   * @returns Promise contenant la réponse Axios ou l'erreur
   */
  createCommerceContact: async (data: IContact) => {
    try {
      const response = axiosInstance.post(
        `restaurants/${idToAddValue.value}/contacts/add`,
        data,
        {
          headers: { 'Content-Type': 'application/json' },
        },
      );
      return response;
    } catch (error) {
      const err = error as AxiosError;
      return Promise.reject(err);
    }
  },

  addStoreContact: async (data: IAddStoreContactPayload) => {
    try {
      const response = axiosInstance.post(
        `restaurants/${commerceIdHorraire.value}/contacts/add`,
        data,
        {
          headers: { 'Content-Type': 'application/json' },
        },
      );
      return response;
    } catch (error) {
      const err = error as AxiosError;
      return Promise.reject(err);
    }
  },

  addStoreContactDirect: async (
    restaurantId: string,
    data: IAddStoreContactPayload,
  ) => {
    try {
      const response = await axiosInstance.post(
        `restaurants/${restaurantId}/contacts/add`,
        data,
        { headers: { 'Content-Type': 'application/json' } },
      );
      return response;
    } catch (error) {
      return Promise.reject(error as AxiosError);
    }
  },

  updateStoreContact: async (
    restaurantId: string,
    contactId: string,
    data: IAddStoreContactPayload,
  ) => {
    try {
      const response = await axiosInstance.put(
        `restaurants/${restaurantId}/contacts/update/${contactId}`,
        data,
        { headers: { 'Content-Type': 'application/json' } },
      );
      return response;
    } catch (error) {
      return Promise.reject(error as AxiosError);
    }
  },

  /**
   * Upload ou met à jour le logo d’un commerce.
   * @param data - Fichier du logo (ICommerceLogo)
   * @returns Promise contenant la réponse Axios ou l'erreur
   */
  createCommerceLogo: async (data: ICommerceLogo) => {
    try {
      const accessToken = token.value?.accessToken;
      const response = await axios.putForm<any>(
        `${VITE_API_URL}files/upload)`,
        data,
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        },
      );
      return response;
    } catch (error) {
      const err = error as AxiosError;
      return Promise.reject(err);
    }
  },

  /**
   * Récupère la liste des commerces avec pagination.
   * @returns Promise contenant la réponse Axios ou l'erreur
   */
  getCommerces: async () => {
    try {
      const params = new URLSearchParams();

      params.append('page', String(Number(toRaw(pageCommerce.value))));
      params.append('pagination', String(toRaw(paginationCommerce.value)));
      params.append('pageSize', String(toRaw(pageSizeCommerce.value)));

      const designation = toRaw(designationCommerce.value);
      const slugVal = toRaw(slugCommerce.value);
      const orgId = toRaw(organizationIdCommerce.value);
      const unaffiliated = toRaw(unaffiliatedCommerce.value);
      const sortBy = toRaw(sortByCommerce.value);
      const order = toRaw(orderCommerce.value);

      if (designation) params.append('designation', designation);
      if (slugVal) params.append('slug', slugVal);
      if (orgId) params.append('organizationId', orgId);
      if (unaffiliated !== null)
        params.append('unaffiliated', String(unaffiliated));
      if (sortBy) params.append('sortBy', sortBy);
      if (order) params.append('order', order);

      const response = await axiosInstance.get<IApiResponseRestaurant>(
        `restaurants/search?${params.toString()}`,
      );
      return response;
    } catch (error) {
      const err = error as AxiosError;
      return Promise.reject(err);
    }
  },

  /**
   * Supprime un commerce par son identifiant.
   * @param restaurantId - ID du commerce à supprimer
   * @returns Promise contenant la réponse Axios ou l'erreur
   */

  /**
   * Met à jour les informations d’un commerce existant.
   * @param data - Données du commerce à mettre à jour (ICommerceBody)
   * @returns Promise contenant la réponse Axios ou l'erreur
   */
  UpdateCommerce: async (data: ICommerceBodyUpdate) => {
    try {
      const response = axiosInstance.put(
        `restaurants/update/${commerceId.value}`,
        data,
        {
          headers: { 'Content-Type': 'application/json' },
        },
      );
      return response;
    } catch (error) {
      const err = error as AxiosError;
      return Promise.reject(err);
    }
  },

  /**
   * Supprime un commerce par son identifiant.
   * @param restaurantId - ID du commerce à supprimer
   * @returns Promise contenant la réponse Axios ou l'erreur
   */

  /**
   * Met à jour les informations d’un commerce existant.
   * @param data - Données du commerce à mettre à jour (ICommerceBody)
   * @returns Promise contenant la réponse Axios ou l'erreur
   */
  UpdateCommerceContact: async (data: any) => {
    try {
      const response = axiosInstance.put(
        `restaurants/${commerceId.value}/contacts/update/${contactId.value}`,
        data,
        {
          headers: { 'Content-Type': 'application/json' },
        },
      );
      return response;
    } catch (error) {
      const err = error as AxiosError;
      return Promise.reject(err);
    }
  },

  /**
   * Supprime un commerce existant à partir de son identifiant.
   * @param restaurantId - Identifiant unique du commerce à supprimer (string)
   * @returns Promise contenant la réponse Axios ou l'erreur
   */
  DeleteCommerce: async (restaurantId: string) => {
    try {
      const response = axiosInstance.delete(
        `restaurants/delete/${restaurantId}`,
        {
          headers: { 'Content-Type': 'application/json' },
        },
      );
      return response;
    } catch (error) {
      const err = error as AxiosError;
      return Promise.reject(err);
    }
  },

  /**
   * Supprime un commerce existant à partir de son identifiant.
   * @param restaurantId - Identifiant unique du commerce à supprimer (string)
   * @returns Promise contenant la réponse Axios ou l'erreur
   */
  DeleteCommerceContact: async () => {
    try {
      const response = axiosInstance.delete(
        `restaurants/${commerceId.value}/contacts/delete/${contactId.value}
`,
        {
          headers: { 'Content-Type': 'application/json' },
        },
      );
      return response;
    } catch (error) {
      const err = error as AxiosError;
      return Promise.reject(err);
    }
  },

  /**
   * Crée un slug unique pour un commerce existant.
   *
   * Cette fonction envoie une requête POST à l'API pour ajouter un slug
   * pour un commerce donné. Le slug est généralement utilisé pour générer
   * des URL lisibles et uniques pour chaque commerce.
   *
   * @async
   * @function createCommerceSlug
   * @param {ICommerceBodySlug} data - Objet contenant les informations nécessaires à la création du slug.
   * @returns {Promise<AxiosResponse>} Une Promise qui résout avec la réponse Axios si la requête réussit,
   * ou rejette avec une instance d'`AxiosError` en cas d'échec.
   *
   * @example
   * const slugData = { commerceId: 1, slug: "mon-restaurant" };
   * createCommerceSlug(slugData)
   *   .then(response => console.log("Slug créé :", response.data))
   *   .catch(error => console.error("Erreur lors de la création du slug :", error));
   */
  createCommerceSlug: async (data: ICommerceBodySlug) => {
    try {
      const response = await axiosInstance.post(`restaurants/slugs/add`, data, {
        headers: { 'Content-Type': 'application/json' },
      });
      return response;
    } catch (error) {
      const err = error as AxiosError;
      return Promise.reject(err);
    }
  },

  /**
   * Récupère la liste des slugs  avec pagination.
   * @returns Promise contenant la réponse Axios ou l'erreur
   */
  getCommerceSlugs: async (): Promise<
    AxiosResponse<IApiResponseAllCommerceSlugs>
  > => {
    try {
      const page = Number(toRaw(pageSlug.value));
      const params = new URLSearchParams();
      const q = qSlug.value;
      const addAfter = addAfterSlug.value;
      const addBefore = addBeforeSlug.value;
      const sortBy = sortBySlug.value;
      const order = orderSlug.value;
      params.append('pagination', 'true');
      params.append('page', page.toString());

      if (q) params.append('q', q.toString());
      if (addAfter) params.append('addAfter', addAfter.toString());
      if (addBefore) params.append('addBefore', addBefore.toString());
      if (sortBy) params.append('sortBy', sortBy.toString());
      if (order) params.append('order', order.toString());

      const response = await axiosInstance.get<IApiResponseAllCommerceSlugs>(
        `restaurants/slugs/search?${params.toString()}`,
      );

      return response;
    } catch (error) {
      const err = error as AxiosError;
      return Promise.reject(err);
    }
  },

  /**
   * Récupère un slugs à partir   son ID.
   * @param id - ID du store
   * @returns Promise contenant la réponse Axios ou l'erreur
   */
  /**
   * Récupère tous les slugs du commerce courant (restaurantId).
   * @returns Promise contenant la réponse Axios ou l'erreur
   */
  getSlugsByStoreId: async (): Promise<AxiosResponse<any>> => {
    try {
      const id = restaurantId.value;
      if (!id) return Promise.reject(new Error('restaurantId manquant'));
      const response = await axiosInstance.get(
        `restaurants/slugs/for-store/${id}`,
      );
      return response;
    } catch (error) {
      return Promise.reject(error as AxiosError);
    }
  },

  getSlugsStoreById: async (): Promise<AxiosResponse<ICommerceSlug>> => {
    try {
      const id = slugStoreId.value;

      if (!id) return;

      const response = await axiosInstance.get<ICommerceSlug>(
        `restaurants/slugs/for-store/${id}`,
      );
      return response;
    } catch (error) {
      const err = error as AxiosError;
      return Promise.reject(err);
    }
  },

  /**
   * Vérifie la disponibilité d’un slug pour un commerce.
   *
   * Cette fonction permet de vérifier si un slug est déjà utilisé
   * ou s’il est encore disponible avant sa création ou mise à jour.
   *
   * @async
   * @function checkAvailabilitySlugStore
   * @returns {Promise<AxiosResponse<IApiResponseCommerceSlugs>>}
   * Une Promise qui résout avec la réponse Axios contenant
   * les informations de disponibilité du slug,
   * ou rejette avec une `AxiosError` en cas d’erreur.
   */
  checkAvailabilitySlugStore: async () => {
    try {
      const slugValue = slug.value;
      if (!slugValue) return;

      const response = await axiosInstance.get<IApiResponseCommerceSlugs>(
        `restaurants/slugs/is-avalaible?slug=${slugValue}`,
      );

      return response;
    } catch (error) {
      const err = error as AxiosError;
      return Promise.reject(err);
    }
  },

  /**
   * Récupère des suggestions de slugs pour un commerce
   * à partir d’un texte donné.
   *
   * Cette fonction interroge l’API afin de générer une liste
   * de slugs candidats basés sur un texte (ex: nom du commerce),
   * avec un nombre de résultats optionnel.
   *
   * @async
   * @function checkSuggestionsSlugStore
   * @returns {Promise<AxiosResponse<IApiResponseCommerceSlugs>>}
   * Une Promise qui résout avec la réponse Axios contenant
   * les slugs suggérés, ou rejette avec une `AxiosError` en cas d’erreur.
   */
  checkSuggestionsSlugStore: async () => {
    try {
      const params = new URLSearchParams();

      const textValue = text.value;
      const countValue = count.value;

      // Ne rien faire si le texte est vide ou non défini
      if (!textValue) return;

      params.append('text', textValue);

      if (countValue) {
        params.append('count', countValue.toString());
      }

      const response = await axiosInstance.get<any>(
        `restaurants/slugs/candidates?${params.toString()}`,
      );

      return response;
    } catch (error) {
      const err = error as AxiosError;
      return Promise.reject(err);
    }
  },

  /**
   * Supprime (désactive) un slug associé à un commerce.
   *
   * Cette méthode envoie une requête PUT à l’API afin de supprimer
   * logiquement un slug existant à partir de son identifiant.
   * La suppression peut correspondre à une désactivation plutôt
   * qu’à une suppression physique en base de données.
   *
   * @async
   * @function updateCommerceSlug
   *
   * @returns {Promise<import('axios').AxiosResponse | void>}
   * Retourne une Promise résolue avec la réponse Axios si la suppression réussit.
   * Retourne `void` si aucun identifiant de slug n’est fourni.
   * Rejette avec une `AxiosError` en cas d’échec de la requête.
   *
   * @throws {import('axios').AxiosError}
   * Levée si une erreur survient lors de l’appel API.
   */
  updateCommerceSlug: async (data: any) => {
    try {
      const id = slugId.value;

      // Ne rien faire si l’identifiant du slug est absent
      if (!id) return;

      const response = await axiosInstance.put(
        `restaurants/slugs/update/${id}`,
        data,
        {
          headers: { 'Content-Type': 'application/json' },
        },
      );

      return response;
    } catch (error) {
      const err = error as AxiosError;
      return Promise.reject(err);
    }
  },

  /**
   * Supprime un slug associé à un commerce à partir de son identifiant.
   *
   * Cette fonction envoie une requête DELETE à l’API afin de supprimer
   * définitivement un slug existant.
   *
   * @async
   * @function deleteCommerceSlug
   * @returns {Promise<AxiosResponse>}
   * Une Promise qui résout avec la réponse Axios si la suppression réussit,
   * ou rejette avec une `AxiosError` en cas d’erreur.
   */
  deleteCommerceSlug: async () => {
    try {
      const id = slugId.value;

      // Ne rien faire si l’identifiant du slug est absent
      if (!id) return;

      const response = await axiosInstance.delete(`slugs/delete/${id}`, {
        headers: { 'Content-Type': 'application/json' },
      });

      return response;
    } catch (error) {
      const err = error as AxiosError;
      return Promise.reject(err);
    }
  },

  /**
   * Récupère la liste de toutes les activités de commerce.
   * @returns Promise contenant la réponse Axios ou l’erreur
   */
  getStoreActivities: async () => {
    try {
      const response = await axiosInstance.get(`store-activities`);
      return response;
    } catch (error) {
      const err = error as AxiosError;
      return Promise.reject(err);
    }
  },

  /**
   * Récupère une activité de commerce par son ID.
   * @param id - Identifiant de l’activité (ex: "BUTCHERY")
   * @returns Promise contenant la réponse Axios ou l’erreur
   */
  getStoreActivityById: async (id: string) => {
    try {
      const response = await axiosInstance.get(`store-activities/${id}`);
      return response;
    } catch (error) {
      const err = error as AxiosError;
      return Promise.reject(err);
    }
  },

  /**
   * Récupère le détail d'un store par son ID.
   * GET /api/restaurants/get/{storeId}
   */
  getStoreById: async (storeId: string) => {
    try {
      const response = await axiosInstance.get(`restaurants/get/${storeId}`);
      return response;
    } catch (error) {
      const err = error as AxiosError;
      return Promise.reject(err);
    }
  },

  /**
   * Statistiques dashboard d'un ou plusieurs stores (totaux agrégés + snapshot/windowed).
   * GET /api/restaurants/stats
   */
  getStoreStats: async (params: {
    storeIds?: string[];
    organizationIds?: string[];
    from?: string;
    to?: string;
  }) => {
    try {
      const qs = new URLSearchParams();
      for (const v of params.storeIds ?? []) qs.append('storeIds', v);
      for (const v of params.organizationIds ?? [])
        qs.append('organizationIds', v);
      if (params.from) qs.append('from', params.from);
      if (params.to) qs.append('to', params.to);
      const response = await axiosInstance.get(
        `restaurants/stats?${qs.toString()}`,
      );
      return response;
    } catch (error) {
      const err = error as AxiosError;
      return Promise.reject(err);
    }
  },
};
